'use client'

import React, { createContext, useContext, useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import {
  fetchAuthSession,
  signIn,
  confirmSignIn,
  signOut,
  getCurrentUser,
} from 'aws-amplify/auth'
import '@/lib/amplify'
import { track, whenAnalyticsAllowed, getAcquisition } from '@/lib/analytics'

// The account was created by this /users/me call, so this is the sign-up,
// whichever page it happened on. The visit source is only saved if the
// visitor allowed analytics cookies (now or later in this page session).
function recordSignUp(apiBase: string) {
  track('sign_up', { method: 'email' }, { conversion: 'signUp' })
  whenAnalyticsAllowed(async () => {
    const acquisition = getAcquisition()
    if (!acquisition) return
    try {
      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()
      if (!token) return
      await fetch(`${apiBase}/marketing/track-event`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ event: 'acquisition', acquisition }),
      })
    } catch { }
  })
}

interface User {
  userId: string
  email: string
  subscriptionTier: 'free' | 'subscriber'
  subscriptionCancelAt?: string
  subscriptionCancelAtPeriodEnd?: boolean
  subscriptionDowngradeTo?: 'free'
  subscriptionBillingPeriod?: 'monthly' | 'quarterly'
  subscriptionPlanId?: 'monthly' | 'quarterly'
  subscriptionStatus?: string
  appliedAffiliateCode?: string
  baseCvId?: string
  baseCoverLetterId?: string
  // Refer a friend (see backend/src/utils/referrals.ts)
  referralCode?: string
  referredByCode?: string
  referralFriends?: number
  referralCreditMinor?: number
  referralCreditCurrency?: string
  referralBonusDays?: number
  bonusProUntil?: string
  // 'referral' when Pro comes only from referral days: there's no
  // subscription behind it to manage.
  proSource?: 'referral'
}

interface AuthContextType {
  user: User | null
  loading: boolean
  loginWithPassword: (email: string, password: string) => Promise<void>
  startEmailOtpSignIn: (email: string) => Promise<void>
  resendEmailOtpSignIn: (email: string) => Promise<void>
  confirmEmailOtpSignIn: (code: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  getAuthToken: () => Promise<string | null>
}

const AUTH_CACHE_KEY = 'cvcircuit:user'

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(AUTH_CACHE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as User
  } catch {
    return null
  }
}

function writeCachedUser(user: User | null) {
  try {
    if (user) {
      localStorage.setItem(AUTH_CACHE_KEY, JSON.stringify(user))
    } else {
      localStorage.removeItem(AUTH_CACHE_KEY)
    }
  } catch { }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== 'undefined') return readCachedUser()
    return null
  })
  // If we have a cached user, start with loading=false so pages don't flash to /login
  const [loading, setLoading] = useState(() => {
    if (typeof window !== 'undefined') return readCachedUser() === null
    return true
  })

  const setUserAndCache = (u: User | null) => {
    writeCachedUser(u)
    setUser(u)
  }

  const forceSignOut = async () => {
    setUserAndCache(null)
    try { await signOut() } catch { }
  }

  const refreshUser = async () => {
    try {
      if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
        setUserAndCache(null)
        setLoading(false)
        return
      }

      // Verify Cognito session is valid
      let cognitoUser
      try {
        cognitoUser = await getCurrentUser()
      } catch {
        // Cognito says not authenticated — clear everything
        setUserAndCache(null)
        setLoading(false)
        return
      }

      const session = await fetchAuthSession()
      const token = session.tokens?.idToken?.toString()

      if (!token) {
        // No token — Cognito session expired, clear
        setUserAndCache(null)
        setLoading(false)
        return
      }

      const apiBase = (process.env.NEXT_PUBLIC_API_URL || '').replace(/\/+$/, '')

      let response: Response
      try {
        response = await fetch(`${apiBase}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        })
      } catch {
        // Network error — keep existing user state, don't log out
        setLoading(false)
        return
      }

      if (response.ok) {
        const { justCreated, ...userData } = await response.json()
        setUserAndCache(userData)
        if (justCreated === true) recordSignUp(apiBase)
      } else if (response.status === 401 || response.status === 403) {
        // Genuine auth failure — sign out
        await forceSignOut()
      } else {
        // Server error or unexpected status — keep existing user, don't sign out
        // The user's Cognito session is valid; this is a backend issue
      }
    } catch (error: any) {
      if (
        error.name === 'UserUnauthenticatedException' ||
        error.name === 'NotAuthorizedException'
      ) {
        // Cognito explicitly says not authenticated
        setUserAndCache(null)
      }
      // Any other error (network, etc.) — keep existing state
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refreshUser()
  }, [])

  // Breadcrumb for global-error.tsx — a root-layout crash's own error only
  // says what broke, not which page the user was actually on when it did,
  // and by the time that boundary mounts the URL may already reflect
  // wherever router.replace()/window.location was heading rather than
  // where the render that actually threw was still showing. AuthProvider is
  // the outermost client component every single route already mounts, so
  // this is the cheapest place to keep a reliably-current answer to that
  // without adding a tracking component of its own to every page.
  const pathname = usePathname()
  useEffect(() => {
    try { sessionStorage.setItem('cvcircuit:lastPathname', pathname) } catch { }
  }, [pathname])

  // Wider net than global-error.tsx's own capture: that one only fires when
  // an error is thrown synchronously during React's render and bubbles up
  // through a component error boundary. The recurring logout crash's actual
  // console signature (minified React #418/#423/#300, stack traces bottoming
  // out in `MessagePort.M`) shows the error is being thrown from inside
  // React's own concurrent scheduler callback — which runs via
  // MessageChannel/postMessage, outside the synchronous call stack an error
  // boundary can see at all. That's why localStorage['cvcircuit:lastGlobalError']
  // stayed null after a real occurrence: global-error.tsx's boundary never
  // mounted for this one. window.onerror/unhandledrejection catch everything
  // that reaches the browser's top level regardless of whether a React
  // boundary was involved, so this is the catch-all underneath that one —
  // same localStorage key, so the existing "check it in devtools" instinct
  // still works. Only the FIRST error per page load is kept (a real crash
  // usually cascades into several follow-on errors; the first is the one
  // worth reading) via the flag below, which starts fresh on each full page
  // load. (It used to live in sessionStorage, which lasts for the whole tab
  // rather than one load, so after one capture nothing later was recorded.)
  //
  // What those three codes turned out to be. #300 was the logout crash:
  // ProfileDropdown returned early before its last two hooks, so the render
  // where logout nulled `user` ran fewer hooks than the one before (fixed
  // there). error.tsx caught it, which is why it never showed up under this
  // key. #418/#423 have nothing to do with logout and are harmless: the
  // cached-user initial state at the top of this component makes a signed-in
  // visitor's first render differ from the pre-rendered HTML, so React drops
  // that HTML and renders the page again on the client. They fire on every
  // signed-in full page load (as #422 where the page sits inside a Suspense
  // boundary, #425 where only text differs), so the whole family is skipped
  // here — otherwise it would take the one slot a real crash needs.
  useEffect(() => {
    let captured = false
    function persistIfFirst(message: string, stack: string | undefined, source: string) {
      try {
        if (captured) return
        if (/Minified React error #(418|422|423|425)\b/.test(message)) return
        captured = true
        const lastPathname = sessionStorage.getItem('cvcircuit:lastPathname')
        localStorage.setItem('cvcircuit:lastGlobalError', JSON.stringify({
          message, stack, source, lastPathname, timestamp: new Date().toISOString(),
        }))
      } catch { }
    }
    function onError(event: ErrorEvent) {
      persistIfFirst(event.message, event.error?.stack, 'window.onerror')
    }
    function onRejection(event: PromiseRejectionEvent) {
      const reason: any = event.reason
      persistIfFirst(reason?.message ?? String(reason), reason?.stack, 'unhandledrejection')
    }
    window.addEventListener('error', onError)
    window.addEventListener('unhandledrejection', onRejection)
    return () => {
      window.removeEventListener('error', onError)
      window.removeEventListener('unhandledrejection', onRejection)
    }
  }, [])

  const loginWithPassword = async (email: string, password: string) => {
    if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
      throw new Error('Authentication is not configured. Please deploy the infrastructure first.')
    }
    try {
      await signIn({ username: email, password })
      await refreshUser()
      const currentUser = await getCurrentUser()
      if (!currentUser) throw new Error('User authentication failed. Please try again.')
    } catch (error: any) {
      if (error.name === 'UserAlreadyAuthenticatedException' || error.message?.includes('already a signed in user')) {
        await refreshUser()
        return
      }
      if (error.name === 'UserNotFoundException' || error.name === 'NotAuthorizedException') {
        throw new Error('Invalid email or password. Please check your credentials and try again.')
      }
      if (error.name === 'UserNotConfirmedException' || error.message?.includes('not confirmed')) {
        throw new Error('Your account is not confirmed. Please check your email for the verification code and confirm your account.')
      }
      throw error
    }
  }

  const startEmailOtpSignIn = async (email: string) => {
    if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
      throw new Error('Authentication is not configured. Please deploy the infrastructure first.')
    }
    await signIn({
      username: email,
      options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
    } as any)
  }

  const resendEmailOtpSignIn = async (email: string) => {
    if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
      throw new Error('Authentication is not configured. Please deploy the infrastructure first.')
    }
    try { await signOut() } catch { }
    await startEmailOtpSignIn(email)
  }

  const confirmEmailOtpSignIn = async (code: string) => {
    if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
      throw new Error('Authentication is not configured. Please deploy the infrastructure first.')
    }
    try {
      const result: any = await confirmSignIn({ challengeResponse: code } as any)
      if (!result || result.isSignedIn !== true) {
        throw new Error('Invalid code. Please try again.')
      }
      await refreshUser()
    } catch (error: any) {
      const name = error?.name || ''
      const message = String(error?.message || '')
      // An expired code (the 15-minute sign-in session ran out) gets the same
      // "Invalid code" as a wrong one, so the message gives nothing away.
      if (
        name === 'NotAuthorizedException' ||
        name === 'CodeMismatchException' ||
        name === 'ExpiredCodeException' ||
        name === 'UserUnauthenticatedException' ||
        /code/i.test(message) ||
        /invalid.*code/i.test(message)
      ) {
        throw new Error('Invalid code. Please try again.')
      }
      throw error
    }
  }

  const logout = async () => {
    if (!process.env.NEXT_PUBLIC_COGNITO_USER_POOL_ID || !process.env.NEXT_PUBLIC_COGNITO_CLIENT_ID) {
      setUserAndCache(null)
      return
    }
    await signOut()
    setUserAndCache(null)
  }

  const getAuthToken = async (): Promise<string | null> => {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch {
      return null
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        loginWithPassword,
        startEmailOtpSignIn,
        resendEmailOtpSignIn,
        confirmEmailOtpSignIn,
        logout,
        refreshUser,
        getAuthToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
